/**
 * THE IDIOMA CLUB SPA MAIN ENTRY POINT
 * ===============================
 * v7.0: React SPA Shell — Single React root with HashRouter.
 * Vanilla views wrapped via VanillaViewBridge, zero flash.
 */
// 🚀 Boot Loader Progress API (defined in index.html inline script)
// @ts-expect-error — __scLoader is a global injected by the bootloader script, not typed on Window
const loader = window.__scLoader;

// 🛡️ Lightweight boot error guard — Sentry loads LAZILY after React mounts
// This saves ~200-300ms of main thread blocking during the critical path.
// Captures boot errors in a queue, flushed to Sentry once it initializes.
const __bootErrors: Array<{ message: string; source?: string; lineno?: number }> = [];
window.onerror = (message, source, lineno) => {
    __bootErrors.push({ message: String(message), source, lineno });
};
// @ts-expect-error — __bootErrors is a global bridge flushed to Sentry after init, not typed on Window

window.__bootErrors = __bootErrors;

import { initChunkErrorHandler, handleBootstrapModuleError } from './core/services/chunkErrorHandler.ts';
import { registry } from './core/services/registry.ts';
import { bootstrapper } from './core/services/Bootstrapper.ts';
import { getSupabaseClient } from './core/services/supabase.ts';
import { roleLandingHash } from './core/domain/role-landing.ts';
import { initPostHog, identifyUser, resetPostHog, schedulePostHogIdleLoad } from './lib/posthog.ts';
import { track } from './shared/analytics/track.ts';
import { ANALYTICS_EVENTS } from './shared/analytics/events.ts';
import sentry from './core/services/sentry.ts';
import { installTapHaptics } from '@theidiomaclub/device/tap-haptics';

// Every tap vibrates, app-wide (packages/device/src/tap-haptics.ts).
installTapHaptics();

// Essential Core for FIRST paint (only what auth + store + supabase need)
import './core/services/constants.ts';
import './core/services/auth.ts';
import './core/services/store.ts';
import { authService } from './core/services/auth.service.ts';
// Import MainRouter so it registers itself in the registry
import './core/services/MainRouter.ts';
import './core/services/sparkly-ports.ts';  // fills the shared Sparkly engine's two host ports
// In the entry stylesheet, not LoginView's chunk: index.html's #door-shell draws the real sheet with it.
import './features/auth/ui/door.css';
import { mainStylesheetReady, shellPosterShown } from './core/ui/bootGates.ts';
import { STUDENT_APP_TARGET, resolveOAuthRedirectTarget } from './core/services/oauthRedirect.ts';
import { hasEmailConfirmLink, redeemEmailConfirmLink } from './email-link.ts';

// Register chunk error recovery before any lazy imports execute
initChunkErrorHandler();

// Init PostHog ASAP — before DOMContentLoaded so no events are lost during boot
initPostHog();

// One-shot per page load — fires after initPostHog() so the queue is ready.
// Synchronous (no dynamic import): a lazy chunk that failed to load would drop
// this event silently — exactly the bug that kept app_opened at 0. track() guards
// against a missing posthog bridge and never throws on the boot path.
{
    const pwa = window.matchMedia('(display-mode: standalone)').matches;
    const mobile = /Mobi|Android/i.test(navigator.userAgent);
    track('app_opened', { device: mobile ? 'mobile' : 'desktop', pwa });
}

// 🚀 DEFERRED: currency-service + realtime load AFTER React mounts.
// CurrencyService loads rates lazily on first use. RealtimeService only
// connects when user reaches the map. Neither blocks first paint.

// 🚀 Pure SPA Mode: Global window leaks eliminated.

// 🛡️ Sentry init is DEFERRED to post-paint: the SDK (~271 kB raw) must never sit
// in the boot-critical path. setUser reads the store AT INIT TIME (not a captured
// variable) so a SIGNED_OUT that happens before init never re-attaches a stale user.
function scheduleDeferredSentryInit(): void {
    const fire = () => {
        void sentry.init().then(() => {
            const store = registry.get<import('./core/services/store').ReactiveStore>('Store');
            const user = store?.get('user') as unknown as { id?: string } | null;
            if (user?.id) {
                // email omitted — PII; Sentry dashboard doesn't need it
                sentry.setUser({ id: user.id });
            }
        });
    };
    // 🚀 PERF: Gate the ~456 kB Sentry SDK behind the FIRST user interaction, with a
    // long idle fallback. This keeps the SDK's ~1.8s of scripting out of the initial
    // load window entirely (Lighthouse navigation audits never interact) while real
    // users still get error tracking the instant they tap — boot-time errors are
    // already queued in window.__bootErrors and flushed on init(). Fallback ensures
    // genuinely-idle sessions (user reading without tapping) still get Sentry.
    let fired = false;
    // Only genuine user-intent events — NOT scroll/visibilitychange, which Lighthouse
    // and the browser can synthesize (would pull the SDK back into the audit window).
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    const trigger = () => {
        if (fired) return;
        fired = true;
        events.forEach((e) => window.removeEventListener(e, trigger));
        fire();
    };
    events.forEach((e) => window.addEventListener(e, trigger, { passive: true }));
    setTimeout(trigger, 8000);
}

// Sparkly is now rendered directly inside BottomNav.tsx (React tree) — no bridge needed.

// Clan Community Sheet — now rendered in BottomNav.tsx (React tree) — no separate root.
// This global function dispatches an event that BottomNav listens for.
(window as unknown as { openClanSheet: () => void }).openClanSheet = function () {
    window.dispatchEvent(new CustomEvent('clan:open'));
};

document.addEventListener('DOMContentLoaded', async () => {
    console.log('🚀 The Idioma Club: Initializing...');
    loader?.update(30, 'Conectando...');

    // 🚀 FAST-TRACK: Login & Reset Password need ZERO auth work — mount React immediately
    // SECURITY: ?reset=true comes from Supabase recovery email. We detect it BEFORE auth touches
    // anything to prevent the OAuth handler from consuming the recovery code and bypassing the
    // password change requirement. If hash got stripped during redirect, we restore it.
    const earlySearchParams = new URLSearchParams(window.location.search);
    const isPasswordResetFlow = earlySearchParams.get('reset') === 'true';
    if (isPasswordResetFlow && !window.location.hash.includes('reset-password')) {
        window.location.hash = '#/reset-password';
    }

    const rawHash = window.location.hash.substring(1);
    const isLoginRoute = /^#\/?login(?:[/?]|$)/.test(window.location.hash);
    const isPlacementRoute = rawHash.includes('placement-test');
    const isResetPasswordRoute = rawHash.includes('reset-password') || isPasswordResetFlow;
    const isPublicRoute = isLoginRoute || isPlacementRoute || isResetPasswordRoute;

    if (isPublicRoute) {
        console.log('⚡ Fast-track: Public route detected, mounting React immediately...');
        loader?.update(60, 'Cargando vista...');
        // Supabase client is already initialized via side-effect import at top of file.
        // NO bootstrapper needed — login only needs React + Router.

        // 🚀 OAuth Callback Detection (BEFORE React mounts)
        const hashParams = new URLSearchParams(rawHash);
        const searchParams = new URLSearchParams(window.location.search);
        
        // OAuth tokens often reside either in the hash (#access_token=) or query (?code=PKCE_CODE)
        const hasImplicitTokens = hashParams.has('access_token') && hashParams.has('refresh_token');
        const hasPkceCode = searchParams.has('code');
        const hasTokens = hasImplicitTokens || hasPkceCode || hasEmailConfirmLink(searchParams);
        // Password recovery owns its own code exchange — bypass the OAuth handler here.
        // Check the ?reset=true flag too in case the hash was stripped during the redirect.
        const isPasswordResetRoute = window.location.hash.startsWith('#/reset-password') || isPasswordResetFlow;

        const supabase = registry.get<{ auth: { setSession: (s: { access_token: string; refresh_token: string }) => Promise<{ data: { session: unknown }; error: unknown }> } }>('Supabase');

        if (hasTokens && supabase && !isPasswordResetRoute) {
            await handleOAuthCallback(hasImplicitTokens ? hashParams : searchParams, supabase);
        }

        // Mount React INSTANTLY — no bootstrapper, no auth, no profile, no prefetch
        const rootEl = document.getElementById('root');
        if (!rootEl) { console.error('❌ #root element not found'); return; }

        if (isLoginRoute && !hasTokens) {
            try {
                // On #/login the entry stylesheet is not render-blocking (vite.config.ts,
                // door-async-main-css); the door needs its tokens before React paints it.
                const [{ mountDoorFirst }] = await Promise.all([import('./core/ui/DoorFirst'), mainStylesheetReady(), shellPosterShown()]);
                mountDoorFirst(rootEl, handleBootstrapModuleError);
            } catch (err) {
                if (handleBootstrapModuleError(err)) return;
                throw err;
            }
            loader?.update(100, '¡Listo!');
            scheduleDeferredSentryInit();
            schedulePostHogIdleLoad();
            return;
        }
        // Import React + ReactDOM + App IN PARALLEL (was 3 serial round-trips).
        let React, createRoot, App;
        try {
            const [reactMod, reactDomClient, appMod] = await Promise.all([
                import('react'),
                import('react-dom/client'),
                import('./App'),
            ]);
            React = reactMod.default;
            ({ createRoot } = reactDomClient);
            ({ App } = appMod);
        } catch (err) {
            if (handleBootstrapModuleError(err)) return;
            throw err;
        }
        loader?.update(100, '¡Listo!');
        await mainStylesheetReady();
        createRoot(rootEl).render(React.createElement(App));

        scheduleDeferredSentryInit();

        schedulePostHogIdleLoad();

        // 🚀 Load CurrencyService — needed by ManualPaymentDetails (profile.billing)
        import('./core/services/currency-service.ts').catch(() => { /* best-effort */ });

        // AppLayout.useEffect calls data-app-ready + loader.done() after first paint
        console.log('✅ The Idioma Club: READY (Fast-track public route)');
        return; // ← Exit early, skip all authenticated-route work
    }

    // ─── AUTHENTICATED ROUTES (Full boot) ───────────────────────────────
    // 🚀 NUCLEAR OPTIMIZATION: initCritical() is near-zero (no repos/use cases needed for React).
    // Auth runs in PARALLEL with critical init — saves ~1729ms of blocked main thread.
    loader?.update(40, 'Buscando tu mapa...');

    // ⚡ WAVE OVERLAP: start fetching React + App chunks NOW, in parallel with the
    // auth/profile network roundtrips (the JS network was idle during them before).
    // The ORIGINAL promise (rejection intact) is awaited inside try/catch below so
    // chunk failures still reach handleBootstrapModuleError; this separate .catch
    // only silences the unhandledrejection of the warming itself.
    const appModulesPromise = Promise.all([
        import('react'),
        import('react-dom/client'),
        import('./App'),
    ]);
    appModulesPromise.catch(() => { /* handled at the await site */ });

    performance.mark('boot-auth-start');
    const [, session] = await Promise.all([
        bootstrapper.initCritical(),
        authService.initialize(),
    ]);
    performance.mark('boot-auth-end');
    performance.measure('Boot:CriticalPath', 'boot-auth-start', 'boot-auth-end');

    // 2. OAuth Callback Detection (BEFORE React mounts)
    const supabase = registry.get<{ auth: { setSession: (s: { access_token: string; refresh_token: string }) => Promise<{ data: { session: unknown }; error: unknown }> } }>('Supabase');
    const hashParams = new URLSearchParams(rawHash);
    const searchParams = new URLSearchParams(window.location.search);
    
    const hasImplicitTokens = hashParams.has('access_token') && hashParams.has('refresh_token');
    const hasPkceCode = searchParams.has('code');
    const hasTokens = hasImplicitTokens || hasPkceCode || hasEmailConfirmLink(searchParams);
    // Password recovery owns its own code exchange — bypass the OAuth handler here.
    // Check the ?reset=true flag too in case the hash was stripped during the redirect.
    const isPasswordResetRoute = window.location.hash.startsWith('#/reset-password') || isPasswordResetFlow;

    if (hasTokens && supabase && !isPasswordResetRoute) {
        await handleOAuthCallback(hasImplicitTokens ? hashParams : searchParams, supabase);
    }

    if (!session) {
        // Adults have their door in /app/; legacy keeps #/login only for the kids app (html[data-kids]).
        if (document.documentElement.dataset.kids !== '1') { window.location.replace('/app/'); return; }
        console.log('🚫 No session found, mounting React at /login...');
        loader?.update(80, 'Redirigiendo a login...');
        window.location.hash = '#/login';

        // Mount React anyway — LoginView will render via the router
        const rootEl = document.getElementById('root');
        if (!rootEl) { console.error('❌ #root element not found'); return; }

        let React, createRoot, App;
        try {
            const [reactMod, reactDomClient, appMod] = await appModulesPromise;
            React = reactMod.default;
            ({ createRoot } = reactDomClient);
            ({ App } = appMod);
        } catch (err) {
            if (handleBootstrapModuleError(err)) return;
            throw err;
        }
        loader?.update(100, '¡Listo!');
        await mainStylesheetReady();
        createRoot(rootEl).render(React.createElement(App));

        scheduleDeferredSentryInit();
        schedulePostHogIdleLoad();

        // AppLayout.useEffect calls data-app-ready + loader.done() after first paint
        console.log('✅ The Idioma Club: READY (redirected to login)');
        return;
    }
    loader?.update(60, 'Cargando tu aventura...');
    console.log('✅ Session verified, loading profile...');

    // 🚀 LOKI FIX: Await prefetchMap alongside getUserProfile — both run in PARALLEL.
    // Before this fix, prefetchMap was fire-and-forget → cache was cold when React
    // mounted → IDLE state → MapSkeleton → data arrives → SUCCESS = FLASH.
    // Now: both finish BEFORE createRoot() → cache warm → instant map render.
    const userRole = (session.user?.app_metadata as Record<string, unknown> | undefined)?.user_role;
    const isStudentUser = !userRole || userRole === 'student';

    // Read currentWorld from store BEFORE prefetch — store is already hydrated from localStorage.
    // Without this, cache.worldId=null but the hook sees currentWorld='some-id' → worldChanged → double fetch.
    const storeForPrefetch = registry.get<import('./core/services/store').ReactiveStore>('Store');
    const prefetchWorldId = (storeForPrefetch?.get('currentWorld') as string | null) ?? undefined;

    // ⚡ Warm the MapModule chunk alongside the data prefetch — removes the wave-3
    // fetch delay when React renders the map route.
    if (isStudentUser) {
        import('./features/map/ui/MapModule').catch(() => { /* chunk warming only */ });
    }

    const mapPrefetchPromise = isStudentUser
        ? import('./features/map/ui/hooks/useMap').then(async ({ prefetchMap }) => {
            const freshSession = await authService.getSession();
            const token = freshSession?.access_token ?? session.access_token;
            if (token) await prefetchMap(token, prefetchWorldId);
        }).catch(() => { /* best-effort — map loads on demand if this fails */ })
        : Promise.resolve();

    const authForProfile = registry.get<{ getUserProfile: () => Promise<unknown> }>('Auth');
    await Promise.all([
        authForProfile?.getUserProfile?.() ?? Promise.resolve(),
        mapPrefetchPromise,
    ]);

    // PostHog: identify user after profile is loaded (queued by the facade if the
    // SDK hasn't loaded yet — replayed in order on flush).
    // Sentry setUser moved into scheduleDeferredSentryInit(): with init deferred to
    // post-paint, a setUser here would hit the not-initialized guard and be dropped.
    {
        const store = registry.get<import('./core/services/store').ReactiveStore>('Store');
        const user = store?.get('user') as unknown as { id?: string; role?: string; status?: string; tenant_id?: string } | null;
        if (user?.id) {
            identifyUser(user.id, {
                role:      user.role      ?? 'student',
                status:    user.status    ?? 'unknown',
                tenant_id: user.tenant_id ?? null,
            });
        }
    }
    // PostHog + Sentry: reset on logout
    getSupabaseClient()?.auth.onAuthStateChange((event) => {
        if (event === 'SIGNED_OUT') {
            resetPostHog();
            sentry.setUser(null);
        }
    });

    // 🚧 ACCESS GATE — a student with no active subscription is handed to /app/,
    // which decides what to show (its own paywall/billing lock) — this app no longer
    // has an enrollment flow of its own. Teachers/admins are exempt (role bypass).
    {
        const store = registry.get<import('./core/services/store').ReactiveStore>('Store');
        const user = store?.get('user') as unknown as { status?: string; role?: string } | null;
        const role = user?.role;
        const isStudent = !role || role === 'student';

        if (user && isStudent && user.status !== 'active') {
            // Si ya hay una suscripción (cualquier status), dejarlo entrar a /map y que
            // AccessGate decida (PaywallScreen si pending_approval/expired).
            const sb = registry.get<{ from: (t: string) => { select: (c: string) => { eq: (c: string, v: string) => { limit: (n: number) => { maybeSingle: () => Promise<{ data: { id: string } | null }> } } } } }>('Supabase');
            const authUser = (store?.get('user') as unknown as { id?: string } | null);
            let hasSub = false;
            if (sb && authUser?.id) {
                try {
                    const res = await sb.from('subscriptions').select('id').eq('user_id', authUser.id).limit(1).maybeSingle();
                    hasSub = !!res.data;
                } catch { /* ignore — fail open to /app/ */ }
            }
            if (!hasSub) {
                console.log('🚧 Non-active student detected — handing off to /app/', { status: user.status });
                window.location.replace('/app/');
                return;
            } else {
                console.log('✅ Non-active student has subscription — letting AccessGate handle access', { status: user.status });
            }
        }
    }

    // 🚀 ROLE-BASED REDIRECT: Send users to their universe BEFORE React mounts.
    // This ensures teachers never see the student map, and admins go straight to admin.
    // Uses JWT claims (already available, zero DB cost).
    //
    // SÓLO PARA QUIEN LLEGA SIN PEDIR RUTA. Antes se evaluaba en cada arranque
    // y le borraba la URL a cualquier admin o profesor: pegabas un enlace
    // directo y la app te dejaba en tu panel. La regla vive en
    // `core/domain/role-landing.ts`, con sus casos.
    const landing = roleLandingHash(rawHash, userRole as string | null | undefined);
    if (landing !== null) {
        window.location.hash = landing;
    }

    loader?.update(90, '¡Casi listo!');
    console.log('✅ Profile loaded, mounting React SPA...');

    // 4. Mount React SPA Shell — IMMEDIATELY after profile
    const rootEl = document.getElementById('root');
    if (!rootEl) {
        console.error('❌ #root element not found in DOM');
        return;
    }

    let React, createRoot, App;
    try {
        const [reactMod, reactDomClient, appMod] = await appModulesPromise;
        React = reactMod.default;
        ({ createRoot } = reactDomClient);
        ({ App } = appMod);
    } catch (err) {
        if (handleBootstrapModuleError(err)) return;
        throw err;
    }

    loader?.update(95, 'Pintando tu mapa...');
    await mainStylesheetReady();
    createRoot(rootEl).render(React.createElement(App));

    // 🛡️ Sentry SDK init deferred to idle — also re-attaches user from the store.
    scheduleDeferredSentryInit();
    schedulePostHogIdleLoad();

    // ⚡ POST-MOUNT: Clan pre-seed (non-blocking, runs after paint)
    // 🎓 ALL roles need Clan — teachers use it for group communication
    requestAnimationFrame(() => {
        try {
            const store = registry.get<import('./core/services/store').ReactiveStore>('Store');
            const user = store?.get('user');
            if (user?.id) {
                import('@theidiomaclub/chat-kit').then(({ preSeedClanUser }) => {
                    preSeedClanUser(user.id, (user as unknown as { tenant_id?: string }).tenant_id ?? null);
                });
            }
        } catch { /* best-effort */ }
    });

    // 🚀 NUCLEAR: Repos/use cases load in background via requestIdleCallback.
    // React is already painted — this NEVER blocks the user.
    // 🛡️ 3-Universe Split: pass userRole so Bootstrapper loads only what’s needed.
    bootstrapper.initBackground(userRole as string | undefined);

    // 🚀 DEFERRED SERVICES: CurrencyService loads for ALL users (admins need it for SubscriptionsFeature,
    // profile.billing users need it for ManualPaymentDetails). RealtimeService only for students.
    // ⚡ Both are independent — load in parallel for faster initialization
    Promise.all([
        import('./core/services/currency-service.ts'),
        ...(isStudentUser ? [import('./core/services/realtime.ts')] : []),
    ]).catch(() => { /* best-effort */ });

    // 🚀 PERFORMANCE: Preload Rank + Profile module chunks AND data during idle time
    // Ensures zero dynamic-import delay AND zero data-fetch delay on first tab visit.
    // 🛡️ Student-only: teachers and admins never visit these tabs.
    if (isStudentUser) {
        const prefetchSecondaryTabs = () => {
            // Preload JS chunks (code-split modules)
            import('./features/rank/ui/RankModule').catch(() => { /* silent */ });
            import('./features/profile/ui/ProfileModule').catch(() => { /* silent */ });

            // Prefetch Rank data into its module-level cache
            import('./features/rank/ui/hooks/useRank').then(({ prefetchRank }) => {
                prefetchRank();
            }).catch(() => { /* best-effort */ });

            // Prefetch Profile data into its module-level cache
            import('./features/profile/ui/hooks/useProfile').then(({ prefetchProfile }) => {
                prefetchProfile();
            }).catch(() => { /* best-effort */ });
        };
        if ('requestIdleCallback' in window) {
            (window as unknown as { requestIdleCallback: (cb: () => void) => void }).requestIdleCallback(prefetchSecondaryTabs);
        } else {
            setTimeout(prefetchSecondaryTabs, 300);
        }
    }

    // 5. Global Event Delegation (Zero onclick Policy)
    // Keep for Vanilla components that still use data-spa-view attributes
    // Uses HashRouter-compatible format: #/viewName (not #viewName)
    document.addEventListener('click', (e) => {
        const target = e.target as HTMLElement;
        const spaBtn = target.closest('[data-spa-view]');
        if (spaBtn) {
            const viewAttr = spaBtn.getAttribute('data-spa-view');
            if (viewAttr) {
                // HashRouter expects #/path format
                const cleanView = viewAttr.replace(/^[#/]+/, '');
                window.location.hash = `#/${cleanView}`;
            }
        }
    });

    // 6. Signal Readiness for E2E Tests — AppLayout.useEffect calls loader.done() after first paint
    console.log('✅ The Idioma Club: READY (React SPA Shell v7.0)');
});

/**
 * TRASPASO AL BACK OFFICE.
 *
 * Esto cargaba el panel DENTRO de esta app: importaba su router, inyectaba su
 * HTML y arrancaba sus repositorios. El panel se mudó a `apps/backoffice`, que
 * tiene su propio arranque, su propio registro y su propia puerta.
 *
 * Se va al mismo origen, así que la sesión de Supabase viaja y nadie vuelve a
 * entrar — es el mismo salto que ya hace el panel del profesor a `/teacher/`.
 */
export function loadAdminModule(): Promise<void> {
    window.location.assign('/admin/');
    return Promise.resolve();   // la navegación se lleva la página; el retorno es contrato, no espera
}

/**
 * 🔐 OAuth Callback Handler
 * Runs BEFORE React mounts to avoid hash conflicts with React Router.
 */
async function handleOAuthCallback(
    params: URLSearchParams,
    supabase: { auth: { setSession: (s: { access_token: string; refresh_token: string }) => Promise<{ data: { session: unknown }; error: unknown }>, exchangeCodeForSession?: (code: string) => Promise<{ data: { session: unknown }; error: unknown }>, verifyOtp?: (p: { token_hash: string; type: 'email' }) => Promise<{ data: { session: unknown }; error: unknown }> } }
): Promise<void> {
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');
    const code = params.get('code');

    try {
        let data, error;
        const emailRes = await redeemEmailConfirmLink(supabase.auth, params);

        if (emailRes) {
            data = emailRes.data;
            error = emailRes.error;
        } else if (code && typeof supabase.auth.exchangeCodeForSession === 'function') {
            console.log('🔄 Exchanging PKCE code for session...');
            const res = await supabase.auth.exchangeCodeForSession(code);
            data = res.data;
            error = res.error;
        } else if (accessToken && refreshToken) {
            const res = await supabase.auth.setSession({
                access_token: accessToken,
                refresh_token: refreshToken
            });
            data = res.data;
            error = res.error;
        } else {
            return;
        }

        if (error) throw error;
        if (data?.session) {
            console.log('✅ OAuth session established');
            if (!emailRes) track('login_success', { method: 'google' });
            // Dynamic import keeps the mark code out of boot; the promise never rejects.
            const pendingMarkWrite = emailRes ? Promise.resolve() : import('./features/auth/infra/googleMark.repo.ts')
                .then((m) => m.stageGooglePendingMark(data.session))
                .catch((err: unknown) => {
                    track(ANALYTICS_EVENTS.DOOR_STORAGE_UNAVAILABLE, { reason: 'stage_failed' });
                    console.error('Could not stage returning mark:', err);
                });
            window.history.replaceState({}, document.title, window.location.pathname);

            // 🛡️ PAYMENT GATE: Never send Google users straight to /map.
            // Check profile status + active subscription before granting access.
            const userId = (data.session as { user?: { id?: string } }).user?.id ?? '';
            const redirectTarget = await resolveOAuthRedirectTarget(userId);
            // Already in flight and never rejects: only guarantees the write lands before the reload.
            await pendingMarkWrite;
            if (redirectTarget === STUDENT_APP_TARGET) {
                // Production: straight to the student app, no kids loader on a reload in between.
                const { handOverToStudentApp } = await import('./features/auth/infra/returningMark.repo.ts');
                handOverToStudentApp();
                return;
            }
            window.location.hash = redirectTarget;
            window.location.reload();
        }
    } catch (err) {
        console.error('❌ OAuth processing error:', err);
    }
}

// 🛠️ DEV: Forward iPhone logs/errors/failed-fetch to the Windows terminal via /__mlog.
// Runs only in vite serve (import.meta.env.DEV is false in the production bundle).
if (import.meta.env.DEV) {
    import('./lib/dev/mobileLogger').then((m) => { m.initMobileLogger(); }).catch(() => { /* noop */ });
}

// 🛠️ DEV UTILITY: Alt + Click to copy any element with a clean HTML and DOM path context
if (import.meta.env.DEV) {
    window.addEventListener('click', (e) => {
        if (e.altKey) {
            e.preventDefault();
            e.stopPropagation();
            const el = e.target as HTMLElement;
            if (!el) return;

            // 1. Generate DOM breadcrumb path selector (super clean)
            const path: string[] = [];
            let current: HTMLElement | null = el;
            while (current && current !== document.body) {
                let name = current.tagName.toLowerCase();
                if (current.id) name += `#${current.id}`;
                else if (current.className) {
                    const firstClass = current.className.split(' ')[0];
                    if (firstClass && !firstClass.startsWith('active:') && !firstClass.startsWith('hover:')) {
                        name += `.${firstClass}`;
                    }
                }
                path.unshift(name);
                current = current.parentElement;
            }
            const pathStr = path.join(' > ');

            // 2. Package everything in a clean, minimal Markdown payload
            const payload = `### 🎯 ELEMENTO UI: <${el.tagName.toLowerCase()}>
- **Ruta DOM:** \`${pathStr}\`

\`\`\`html
${el.outerHTML}
\`\`\`
`;

            navigator.clipboard.writeText(payload).then(() => {
                console.log('📋 [DevHelper] Elemento copiado:', el, payload);
                
                // Dynamic import to avoid circular dependencies during boot
                import('./core/services/notify.ts').then((module) => {
                    module.default.success(`¡Copiado HTML de <${el.tagName.toLowerCase()}>!`);
                }).catch(() => {
                    console.log('📋 Copiado con éxito');
                });
            }).catch((err) => {
                console.error('❌ [DevHelper] Error al copiar al portapapeles:', err);
            });
        }
    }, { capture: true });
}

